// src/utils/mindmapPdf.js
// PDF-Export der Research-Mindmap (US-RES-MINDMAP-PDF, ADR 0065).
//
// Ablauf in drei Schritten, bewusst getrennt:
//  1. snapshotMindmap(canvas): liest die Geometrie der GERENDERTEN Mindmap aus
//     dem DOM (Label-Rechtecke, Textzeilen, Badges, Klapp-Pfeile, Linien) in
//     ein reines Datenobjekt in CSS-Pixeln. Damit gilt exakt die aktuelle
//     Ansicht (zugeklappte Äste, Suchfilter) und das Flex-Layout muss nicht
//     nachgebaut werden. Zeilenumbrüche werden pro Wort über Range-Rechtecke
//     ermittelt, damit das PDF genau so umbricht wie der Bildschirm.
//  2. Layout-Rechnung (fitSinglePage / computeTiles): reine Funktionen, die der
//     Dialog live für die Vorschau (Massstab, Blattanzahl, Schriftgrösse) nutzt.
//  3. exportMindmapPdf(...): zeichnet den Snapshot als Vektorgrafik mit jsPDF —
//     entweder skaliert auf ein Blatt oder als Kacheln (Einzelblätter):
//     linksbündig ab Blatt 1 oben links, Schnitte in Lücken zwischen Knoten,
//     leere Blätter entfallen, Schnittkanten mit Anschluss-Hinweis, optionale
//     Überlappung und Übersichtsblatt. Vektor statt Rasterbild,
//     weil grosse Mindmaps als Canvas an Browser-Grenzen (max. Pixelfläche)
//     stossen und Text bei A0/Kacheln scharf bleiben muss.
//
// Farben: das PDF nutzt immer eine helle Druckpalette (weisse Fragen-Knoten,
// dunkle Schrift), unabhängig vom Dark-/Light-Theme der App. Kapitel- und
// Unterkapitelfarben kommen aus den Inline-Styles der Knoten.

import { loadJsPdf } from "./loadJsPdf.js";

export const PX_MM = 25.4 / 96; // 1 CSS-Pixel in mm bei 100 %
const MM_PT = 72 / 25.4;

export const PAPER_FORMATS = {
  A4: [210, 297],
  A3: [297, 420],
  A2: [420, 594],
  A1: [594, 841],
  A0: [841, 1189],
};

export const PAGE_MARGIN = 10; // mm Rand rundum (Kopfzeile liegt im oberen Rand)

// Voreingestellte Massstäbe für die Einzelblatt-Übersicht im Dialog.
export const TILE_SCALE_PRESETS = [25, 40, 50, 60, 75, 100];

// Obergrenze der Einzelblätter — darüber wird das PDF unhandlich und der
// Export blockiert den Browser spürbar.
export const MAX_TILES = 400;

const WHITE = [255, 255, 255];
const PRINT = {
  qFill: WHITE,
  qStroke: [201, 206, 214],
  text: [31, 35, 40],
  muted: [122, 129, 140],
  link: [160, 167, 177],
  header: [110, 116, 125],
  grid: [46, 109, 164],
};

// --- Farben -------------------------------------------------------------------

// "rgb(...)"/"rgba(...)"/"#rrggbb" -> [r,g,b], Alpha auf `under` (Default weiss)
// gemischt. Unbekannt -> null.
export const parseColor = (str, under = WHITE) => {
  if (!str) return null;
  const s = String(str).trim();
  let r;
  let g;
  let b;
  let a = 1;
  const m = s.match(/rgba?\(([^)]+)\)/i);
  if (m) {
    const parts = m[1].split(/[\s,/]+/).filter(Boolean).map(parseFloat);
    [r, g, b] = parts;
    if (parts.length > 3 && !Number.isNaN(parts[3])) a = parts[3];
  } else if (/^#[0-9a-f]{6}$/i.test(s)) {
    const n = parseInt(s.slice(1), 16);
    r = n >> 16;
    g = (n >> 8) & 255;
    b = n & 255;
  } else {
    return null;
  }
  if ([r, g, b].some((v) => Number.isNaN(v))) return null;
  return [0, 1, 2].map((i) => Math.round([r, g, b][i] * a + under[i] * (1 - a)));
};

// --- Text ---------------------------------------------------------------------

// Die jsPDF-Standardschrift (Helvetica, WinAnsi) kennt nur Latin-1 plus die
// cp1252-Sonderzeichen. Alles andere (Emojis, Pfeile, CJK) würde als
// Zeichensalat erscheinen und wird durch "?" ersetzt.
const CP1252_EXTRA = new Set(
  Array.from("€‚ƒ„…†‡ˆ‰Š‹ŒŽ‘’“”•–—˜™š›œžŸ").map((c) => c.codePointAt(0))
);
export const toPdfText = (str) =>
  Array.from(String(str || ""))
    .map((ch) => {
      const cp = ch.codePointAt(0);
      if (cp === 0x2212) return "-";
      if (cp === 0x00a0 || cp === 0x202f || cp === 0x2009) return " ";
      if (cp <= 0xff || CP1252_EXTRA.has(cp)) return ch;
      return "?";
    })
    .join("");

// Zerlegt den Text eines Elements in die tatsächlich gerenderten Zeilen:
// jedes Wort über eine Range vermessen, Wörter mit gleicher Oberkante bilden
// eine Zeile. Rückgabe in Viewport-Koordinaten.
const measureLines = (el) => {
  const lines = [];
  const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
  const range = document.createRange();
  let node = walker.nextNode();
  while (node) {
    const text = node.nodeValue || "";
    const re = /\S+/g;
    let m = re.exec(text);
    while (m) {
      range.setStart(node, m.index);
      range.setEnd(node, m.index + m[0].length);
      const r = range.getBoundingClientRect();
      if (r.width > 0 || r.height > 0) {
        const last = lines[lines.length - 1];
        if (last && Math.abs(r.top - last.top) < last.height / 2) {
          last.words.push(m[0]);
          last.right = Math.max(last.right, r.right);
        } else {
          lines.push({ words: [m[0]], left: r.left, right: r.right, top: r.top, height: r.height });
        }
      }
      m = re.exec(text);
    }
    node = walker.nextNode();
  }
  range.detach?.();
  return lines;
};

const fontOf = (el) => {
  const cs = window.getComputedStyle(el);
  return {
    size: parseFloat(cs.fontSize) || 13,
    bold: (parseInt(cs.fontWeight, 10) || 400) >= 600,
    italic: cs.fontStyle === "italic",
  };
};

// --- 1. Snapshot ----------------------------------------------------------------

/**
 * Liest die aktuell sichtbare Mindmap aus dem DOM.
 * @param {HTMLElement} canvas  das `.mindmap-canvas`-Element
 * @returns {{width:number,height:number,nodes:Array,links:Array,bodyFontPx:number}}
 */
export function snapshotMindmap(canvas) {
  const cb = canvas.getBoundingClientRect();
  const rel = (r) => ({ x: r.left - cb.left, y: r.top - cb.top, w: r.width, h: r.height });
  const visible = (el) => {
    const r = el.getBoundingClientRect();
    return r.width > 0 && r.height > 0;
  };

  const nodes = [];
  let bodyFontPx = null;

  canvas.querySelectorAll(".mindmap-lbl").forEach((el) => {
    if (!visible(el)) return;
    const cls = el.classList;
    const type = cls.contains("mindmap-root")
      ? "root"
      : cls.contains("mindmap-chap")
        ? "chap"
        : cls.contains("mindmap-sub")
          ? "sub"
          : "q";

    let fill;
    let stroke;
    let bar = null;
    let textColor = PRINT.text;
    if (type === "root" || type === "chap") {
      fill = parseColor(el.style.backgroundColor) || [31, 56, 100];
      stroke = fill;
      textColor = WHITE;
    } else if (type === "sub") {
      fill = parseColor(el.style.backgroundColor) || [240, 240, 240];
      stroke = parseColor(el.style.borderColor || el.style.borderTopColor) || PRINT.qStroke;
    } else {
      fill = PRINT.qFill;
      stroke = PRINT.qStroke;
      bar = parseColor(el.style.borderLeftColor);
    }

    const node = { type, ...rel(el.getBoundingClientRect()), fill, stroke, bar, texts: [] };

    // Feste ID und laufende Nummer (nur Fragen) — einzeilig, gedämpft.
    el.querySelectorAll(":scope > .mindmap-q-id, :scope > .mindmap-q-nr").forEach((s) => {
      if (!visible(s)) return;
      const f = fontOf(s);
      const r = rel(s.getBoundingClientRect());
      const lines = measureLines(s);
      const lr = lines[0] ? rel({ left: lines[0].left, top: lines[0].top, width: lines[0].right - lines[0].left, height: lines[0].height }) : r;
      node.texts.push({ text: s.textContent.trim(), x: lr.x, cy: lr.y + lr.h / 2, w: lr.w, ...f, color: PRINT.muted });
    });

    // Beschriftung: Zeile für Zeile wie auf dem Bildschirm.
    const cap = el.querySelector(":scope > .mindmap-cap");
    if (cap) {
      const f = fontOf(cap);
      const untitled = !!cap.querySelector(".mindmap-untitled");
      if (type === "q" && !untitled && bodyFontPx == null) bodyFontPx = f.size;
      measureLines(cap).forEach((ln) => {
        const r = rel({ left: ln.left, top: ln.top, width: ln.right - ln.left, height: ln.height });
        node.texts.push({
          text: ln.words.join(" "),
          x: r.x,
          cy: r.y + r.h / 2,
          w: r.w,
          ...f,
          italic: f.italic || untitled,
          color: untitled ? PRINT.muted : textColor,
        });
      });
    }

    const toggle = el.querySelector(":scope > .mindmap-toggle");
    if (toggle && visible(toggle)) {
      const r = rel(toggle.getBoundingClientRect());
      const f = fontOf(toggle);
      node.toggle = {
        x: r.x,
        cy: r.y + r.h / 2,
        size: f.size,
        collapsed: toggle.textContent.includes("▶"),
        color: textColor,
      };
    }

    const badge = el.querySelector(":scope > .mindmap-badge");
    if (badge && visible(badge)) {
      const r = rel(badge.getBoundingClientRect());
      const f = fontOf(badge);
      // Kapitel-Badge ist halbtransparent weiss über der Kapitelfarbe.
      const bfill =
        parseColor(badge.style.backgroundColor, fill) ||
        parseColor(window.getComputedStyle(badge).backgroundColor, fill) ||
        fill;
      node.badge = { ...r, text: badge.textContent.trim(), fill: bfill, size: f.size };
    }

    nodes.push(node);
  });

  // Verbindungslinien — gleiche Logik wie drawLinks in ResearchMindmap.
  const links = [];
  canvas.querySelectorAll(".mindmap-node").forEach((n) => {
    if (n.classList.contains("collapsed")) return;
    const plbl = n.querySelector(":scope > .mindmap-lbl");
    const kids = n.querySelector(":scope > .mindmap-kids");
    if (!plbl || !kids || !visible(plbl)) return;
    const a = plbl.getBoundingClientRect();
    [...kids.children].forEach((cn) => {
      if (!cn.classList.contains("mindmap-node")) return;
      const clbl = cn.querySelector(":scope > .mindmap-lbl");
      if (!clbl || !visible(clbl)) return;
      const b = clbl.getBoundingClientRect();
      links.push({
        x1: a.right - cb.left,
        y1: a.top - cb.top + a.height / 2,
        x2: b.left - cb.left,
        y2: b.top - cb.top + b.height / 2,
      });
    });
  });

  // Auf die Knoten zuschneiden (der Canvas hat unten/rechts Leerraum für den FAB).
  const PAD = 6;
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  nodes.forEach((n) => {
    minX = Math.min(minX, n.x);
    minY = Math.min(minY, n.y);
    maxX = Math.max(maxX, n.x + n.w);
    maxY = Math.max(maxY, n.y + n.h);
  });
  if (!nodes.length) {
    minX = 0;
    minY = 0;
    maxX = 1;
    maxY = 1;
  }
  const dx = PAD - minX;
  const dy = PAD - minY;
  nodes.forEach((n) => {
    n.x += dx;
    n.y += dy;
    n.texts.forEach((t) => {
      t.x += dx;
      t.cy += dy;
    });
    if (n.toggle) {
      n.toggle.x += dx;
      n.toggle.cy += dy;
    }
    if (n.badge) {
      n.badge.x += dx;
      n.badge.y += dy;
    }
  });
  links.forEach((l) => {
    l.x1 += dx;
    l.x2 += dx;
    l.y1 += dy;
    l.y2 += dy;
  });

  return {
    width: maxX - minX + 2 * PAD,
    height: maxY - minY + 2 * PAD,
    nodes,
    links,
    bodyFontPx: bodyFontPx || 13.28,
  };
}

// --- 2. Layout-Rechnung ---------------------------------------------------------

export const pageSize = (format, orientation) => {
  const [a, b] = PAPER_FORMATS[format] || PAPER_FORMATS.A4;
  return orientation === "portrait" ? [a, b] : [b, a];
};

const contentArea = (format, orientation) => {
  const [pw, ph] = pageSize(format, orientation);
  return [pw - 2 * PAGE_MARGIN, ph - 2 * PAGE_MARGIN];
};

// Schriftgrösse der Fragen im PDF (pt) bei einem Massstab in mm/px.
export const fontPtAt = (snap, mmPerPx) => snap.bodyFontPx * mmPerPx * MM_PT;

// Ein Blatt: Massstab so, dass die ganze Mindmap in die Nutzfläche passt.
// orientation "auto" wählt die Ausrichtung mit dem grösseren Massstab.
export function fitSinglePage(snap, format, orientation = "auto") {
  const calc = (o) => {
    const [aw, ah] = contentArea(format, o);
    const mmPerPx = Math.min(aw / snap.width, ah / snap.height);
    return { orientation: o, mmPerPx };
  };
  const res =
    orientation === "auto"
      ? [calc("landscape"), calc("portrait")].sort((x, y) => y.mmPerPx - x.mmPerPx)[0]
      : calc(orientation);
  return {
    ...res,
    percent: (res.mmPerPx / PX_MM) * 100,
    fontPt: fontPtAt(snap, res.mmPerPx),
  };
}

// Teilt eine Achse (0..total, px) in Abschnitte von höchstens `area` px.
// Der Schnitt wird nicht starr bei `area` gesetzt, sondern in der hinteren
// Hälfte des Fensters an die Stelle, die am wenigsten Knoten durchschneidet
// (bei Gleichstand die späteste) — so liegen die Blattübergänge möglichst in
// den Lücken zwischen den Knoten. `ov` = Überlappung: der nächste Abschnitt
// beginnt `ov` px vor dem Schnitt.
// Anzahl Werte in sortiertem Array `arr`, die < x (strict) bzw. <= x sind.
const countBelow = (arr, x, inclusive) => {
  let lo = 0;
  let hi = arr.length;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (arr[mid] < x || (inclusive && arr[mid] === x)) lo = mid + 1;
    else hi = mid;
  }
  return lo;
};

const splitAxis = (intervals, total, area, ov) => {
  const starts = intervals.map((i) => i[0]).sort((a, b) => a - b);
  const ends = intervals.map((i) => i[1]).sort((a, b) => a - b);
  // Knoten, die die Stelle c durchschneidet: a < c < b  ==  #(a < c) - #(b <= c).
  const crossings = (c) => countBelow(starts, c, false) - countBelow(ends, c, true);
  const segs = [];
  let start = 0;
  // Sicherheitsgrenze gegen Endlosschleifen bei entarteten Eingaben.
  for (let guard = 0; guard < 2000; guard += 1) {
    if (total - start <= area) {
      segs.push([start, total]);
      return segs;
    }
    const hi = start + area;
    const lo = start + area * 0.5;
    const cands = [hi];
    intervals.forEach(([a, b]) => {
      if (a - 2 > lo && a - 2 < hi) cands.push(a - 2);
      if (b + 2 > lo && b + 2 < hi) cands.push(b + 2);
    });
    let best = hi;
    let bestCost = Infinity;
    cands.forEach((c) => {
      const cost = crossings(c);
      if (cost < bestCost || (cost === bestCost && c > best)) {
        best = c;
        bestCost = cost;
      }
    });
    segs.push([start, best]);
    start = best - ov;
  }
  segs.push([start, total]);
  return segs;
};

// Einzelblätter bei gegebenem Massstab (%) und Überlappung (mm). Die Mindmap
// beginnt oben links auf Blatt 1 (linksbündig, kein Zentrieren), Spalten- und
// Zeilenschnitte gelten für das ganze Raster, damit die Blätter nebeneinander
// und untereinander nahtlos anschliessen. Blätter ohne Inhalt entfallen.
export function computeTiles(snap, format, orientation, percent, overlapMm = 0) {
  const mmPerPx = (PX_MM * percent) / 100;
  const [aw, ah] = contentArea(format, orientation);
  const ov = Math.max(0, Math.min(overlapMm, Math.min(aw, ah) / 3));
  const xSegs = splitAxis(
    snap.nodes.map((n) => [n.x, n.x + n.w]),
    snap.width,
    aw / mmPerPx,
    ov / mmPerPx
  );
  const ySegs = splitAxis(
    snap.nodes.map((n) => [n.y, n.y + n.h]),
    snap.height,
    ah / mmPerPx,
    ov / mmPerPx
  );
  const boxes = [
    ...snap.nodes.map((n) => ({ x: n.x, y: n.y, w: n.w, h: n.h })),
    ...snap.links.map((l) => ({
      x: Math.min(l.x1, l.x2),
      y: Math.min(l.y1, l.y2),
      w: Math.abs(l.x2 - l.x1) + 1,
      h: Math.abs(l.y2 - l.y1) + 1,
    })),
  ];
  const cells = [];
  ySegs.forEach(([y0, y1], r) => {
    xSegs.forEach(([x0, x1], c) => {
      const rect = { x: x0, y: y0, w: x1 - x0, h: y1 - y0 };
      cells.push({ r, c, x0, x1, y0, y1, empty: !boxes.some((b) => intersects(b, rect)) });
    });
  });
  let nr = 0;
  cells.forEach((cell) => {
    cell.nr = cell.empty ? null : (nr += 1);
  });
  return {
    mmPerPx,
    percent,
    orientation,
    cols: xSegs.length,
    rows: ySegs.length,
    cells,
    pages: cells.filter((c) => !c.empty),
    count: nr,
    areaW: aw,
    areaH: ah,
    overlap: ov,
    fontPt: fontPtAt(snap, mmPerPx),
  };
}

// Grösster Massstab (ganze Prozent), bei dem die Mindmap auf `maxSheets`
// Blätter passt — für den Hinweis "1 Blatt bis X %".
export function maxPercentForSheets(snap, format, orientation, maxSheets, overlapMm = 0) {
  let lo = 1;
  let hi = 400;
  if (computeTiles(snap, format, orientation, lo, overlapMm).count > maxSheets) return null;
  while (lo < hi) {
    const mid = Math.ceil((lo + hi) / 2);
    if (computeTiles(snap, format, orientation, mid, overlapMm).count <= maxSheets) lo = mid;
    else hi = mid - 1;
  }
  return lo;
}

// --- 3. Zeichnen ----------------------------------------------------------------

const intersects = (a, b) =>
  a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y;

// Zeichnet die ganze Mindmap mit Ursprung (ox, oy) in mm und Massstab s (mm/px).
// `view` (mm, Seitenkoordinaten) begrenzt auf sichtbare Elemente (Kacheln).
function drawMap(doc, snap, ox, oy, s, view) {
  const X = (px) => ox + px * s;
  const Y = (px) => oy + px * s;
  const inView = (x, y, w, h) =>
    !view || intersects({ x: X(x), y: Y(y), w: w * s, h: h * s }, view);

  // Linien zuerst (unter den Knoten).
  doc.setDrawColor(...PRINT.link);
  doc.setLineWidth(Math.max(0.1, 1.6 * s));
  snap.links.forEach((l) => {
    const minX = Math.min(l.x1, l.x2);
    const minY = Math.min(l.y1, l.y2);
    if (!inView(minX, minY, Math.abs(l.x2 - l.x1) + 1, Math.abs(l.y2 - l.y1) + 1)) return;
    const mx = l.x1 + (l.x2 - l.x1) / 2;
    doc.moveTo(X(l.x1), Y(l.y1));
    doc.curveTo(X(mx), Y(l.y1), X(mx), Y(l.y2), X(l.x2), Y(l.y2));
    doc.stroke();
  });

  snap.nodes.forEach((n) => {
    if (!inView(n.x, n.y, n.w, n.h)) return;
    const x = X(n.x);
    const y = Y(n.y);
    const w = n.w * s;
    const h = n.h * s;
    const r = Math.min(10 * s, h / 2, w / 2);
    const lw = Math.max(0.08, 1 * s);

    doc.setLineWidth(lw);
    doc.setFillColor(...n.fill);
    doc.setDrawColor(...n.stroke);
    doc.roundedRect(x, y, w, h, r, r, "FD");

    // Farbiger linker Balken der Fragen (CSS border-left), an den Rundungen
    // über einen Clip-Pfad sauber abgeschnitten.
    if (n.bar) {
      doc.saveGraphicsState();
      doc.roundedRect(x, y, w, h, r, r, null);
      doc.clip();
      doc.discardPath();
      doc.setFillColor(...n.bar);
      doc.rect(x, y, 4 * s, h, "F");
      doc.restoreGraphicsState();
    }

    if (n.toggle) {
      const t = n.toggle;
      const g = t.size * 0.62 * s; // Glyph-Grösse
      const tx = X(t.x) + 1 * s;
      const tc = Y(t.cy);
      doc.setFillColor(...t.color);
      if (t.collapsed) doc.triangle(tx, tc - g / 2, tx, tc + g / 2, tx + g * 0.85, tc, "F");
      else doc.triangle(tx, tc - g * 0.42, tx + g, tc - g * 0.42, tx + g / 2, tc + g * 0.45, "F");
    }

    n.texts.forEach((t) => {
      const style = t.bold && t.italic ? "bolditalic" : t.bold ? "bold" : t.italic ? "italic" : "normal";
      doc.setFont("helvetica", style);
      const text = toPdfText(t.text);
      let pt = t.size * s * MM_PT;
      doc.setFontSize(pt);
      // Helvetica läuft minimal anders als die Bildschirmschrift: ist die
      // Zeile breiter als auf dem Bildschirm, wird sie proportional verkleinert
      // statt über den Knotenrand hinauszulaufen.
      const target = t.w * s;
      const measured = doc.getTextWidth(text);
      if (target > 0 && measured > target * 1.01) {
        pt *= target / measured;
        doc.setFontSize(pt);
      }
      doc.setTextColor(...t.color);
      doc.text(text, X(t.x), Y(t.cy), { baseline: "middle" });
    });

    if (n.badge) {
      const b = n.badge;
      const bx = X(b.x);
      const by = Y(b.y);
      const bw = b.w * s;
      const bh = b.h * s;
      doc.setFillColor(...b.fill);
      doc.roundedRect(bx, by, bw, bh, bh / 2, bh / 2, "F");
      doc.setFont("helvetica", "bold");
      doc.setFontSize(b.size * s * MM_PT);
      doc.setTextColor(...WHITE);
      doc.text(toPdfText(b.text), bx + bw / 2, by + bh / 2, { baseline: "middle", align: "center" });
    }
  });
}

const drawHeader = (doc, pw, left, right) => {
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7);
  doc.setTextColor(...PRINT.header);
  doc.text(toPdfText(left), PAGE_MARGIN, PAGE_MARGIN / 2 + 1, { baseline: "middle" });
  if (right) {
    doc.text(toPdfText(right), pw - PAGE_MARGIN, PAGE_MARGIN / 2 + 1, {
      baseline: "middle",
      align: "right",
    });
  }
};

// Beschriftung der exportierten Ebenen für Kopfzeile und Dialog. `states` =
// Chip-Zustände je Ebene ("on" | "off", Index 0 = Ebene 1); abgewählte Ebenen
// sind ausgeblendet (nicht eingeklappt). `anyCollapsed` = einzelne Äste zu.
export const levelsLabel = (states, anyCollapsed = false) => {
  const shown = states.map((st, i) => (st === "on" ? i + 1 : null)).filter(Boolean);
  const base =
    shown.length === states.length
      ? "alle Ebenen"
      : shown.length === 0
        ? "nur Wurzel"
        : `${shown.length === 1 ? "Ebene" : "Ebenen"} ${shown.join(", ")} von ${states.length}`;
  return anyCollapsed ? `${base}, teilweise zugeklappt` : base;
};

const fmtPercent = (p) => `${Math.round(p)} %`;

const todayCH = () => {
  const d = new Date();
  return `${String(d.getDate()).padStart(2, "0")}.${String(d.getMonth() + 1).padStart(2, "0")}.${d.getFullYear()}`;
};

const filenamePart = (s) => (s || "").toString().trim().replace(/[^\w-]+/g, "_");

/**
 * Erzeugt und speichert das PDF.
 * @param {object} snap      Ergebnis von snapshotMindmap
 * @param {object} opts
 *   mode: "single" | "tiles"
 *   format: "A4".."A0"
 *   orientation: "auto" | "landscape" | "portrait" (auto nur bei "single")
 *   percent: Massstab in % (nur "tiles")
 *   overlapMm: Überlappung der Kacheln in mm (nur "tiles")
 *   overview: Übersichtsblatt mit Kachelplan voranstellen (nur "tiles")
 *   title: Projektname für Kopfzeile und Dateiname
 *   viewLabel: Beschreibung der exportierten Ansicht (z.B. "bis Ebene 2 von 4")
 */
export async function exportMindmapPdf(snap, opts) {
  const jsPDF = await loadJsPdf();
  const { mode, format, title } = opts;
  const head = `Mindmap · ${title || "Research"}${opts.viewLabel ? ` · ${opts.viewLabel}` : ""} · Stand ${todayCH()}`;

  if (mode === "single") {
    const fit = fitSinglePage(snap, format, opts.orientation || "auto");
    const [pw, ph] = pageSize(format, fit.orientation);
    const doc = new jsPDF({ unit: "mm", format: [pw, ph], orientation: fit.orientation });
    const [aw, ah] = contentArea(format, fit.orientation);
    const ox = PAGE_MARGIN + (aw - snap.width * fit.mmPerPx) / 2;
    const oy = PAGE_MARGIN + (ah - snap.height * fit.mmPerPx) / 2;
    drawHeader(doc, pw, head, `${format} · Massstab ${fmtPercent(fit.percent)}`);
    drawMap(doc, snap, ox, oy, fit.mmPerPx, null);
    doc.save(`Mindmap_${filenamePart(title)}_${format}.pdf`);
    return;
  }

  const orientation = opts.orientation === "portrait" ? "portrait" : "landscape";
  const t = computeTiles(snap, format, orientation, opts.percent, opts.overlapMm || 0);
  if (t.count > MAX_TILES) {
    throw new Error(`Zu viele Blätter (${t.count}). Bitte Massstab verkleinern oder grösseres Format wählen.`);
  }
  const [pw, ph] = pageSize(format, orientation);
  const doc = new jsPDF({ unit: "mm", format: [pw, ph], orientation });
  const s = t.mmPerPx;
  const cellAt = (r, c) => t.cells.find((x) => x.r === r && x.c === c);
  const gridInfo = `${t.cols} × ${t.rows}`;

  let first = true;
  if (opts.overview && t.count > 1) {
    // Übersichtsblatt: ganze Mindmap verkleinert, darüber die Blattgrenzen
    // mit Blattnummern als Montagehilfe (leere Rasterfelder entfallen).
    const k = Math.min(t.areaW / snap.width, t.areaH / snap.height);
    const gx = PAGE_MARGIN;
    const gy = PAGE_MARGIN;
    drawHeader(doc, pw, head, `Übersicht · ${t.count} Blätter ${format} (Raster ${gridInfo}) · Massstab ${fmtPercent(t.percent)}`);
    drawMap(doc, snap, gx, gy, k, null);
    doc.setDrawColor(...PRINT.grid);
    doc.setLineWidth(0.25);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...PRINT.grid);
    t.pages.forEach((cell) => {
      const x = gx + cell.x0 * k;
      const y = gy + cell.y0 * k;
      const w = (cell.x1 - cell.x0) * k;
      const h = (cell.y1 - cell.y0) * k;
      doc.rect(x, y, w, h, "S");
      doc.setFontSize(Math.max(6, Math.min(28, Math.min(w, h) * MM_PT * 0.15)));
      doc.text(String(cell.nr), x + 1.5, y + 1.5, { baseline: "top" });
    });
    first = false;
  }

  const CUT = [46, 109, 164];
  t.pages.forEach((cell) => {
    if (!first) doc.addPage([pw, ph], orientation);
    first = false;
    // Linksbündig: der Ausschnitt beginnt immer oben links in der Nutzfläche.
    const w = (cell.x1 - cell.x0) * s;
    const h = (cell.y1 - cell.y0) * s;
    const view = { x: PAGE_MARGIN, y: PAGE_MARGIN, w, h };
    drawHeader(
      doc,
      pw,
      head,
      `Blatt ${cell.nr} von ${t.count} · Zeile ${cell.r + 1}, Spalte ${cell.c + 1} · Massstab ${fmtPercent(t.percent)}`
    );
    doc.saveGraphicsState();
    doc.rect(view.x, view.y, view.w, view.h, null);
    doc.clip();
    doc.discardPath();
    drawMap(doc, snap, PAGE_MARGIN - cell.x0 * s, PAGE_MARGIN - cell.y0 * s, s, view);
    doc.restoreGraphicsState();

    // Feiner Rahmen des Ausschnitts: links/oben ist die Anlegekante für das
    // vorangehende Blatt.
    doc.setDrawColor(215, 219, 224);
    doc.setLineWidth(0.1);
    doc.rect(view.x, view.y, view.w, view.h, "S");

    doc.setFont("helvetica", "normal");
    doc.setFontSize(6.5);
    doc.setTextColor(...CUT);
    doc.setDrawColor(...CUT);
    doc.setLineWidth(0.2);
    doc.setLineDashPattern([1.5, 1], 0);
    // Rechts/unten: gestrichelte Schnittkante, wenn dort ein Blatt anschliesst.
    const right = cellAt(cell.r, cell.c + 1);
    if (right && !right.empty) {
      doc.line(view.x + w, view.y, view.x + w, view.y + h);
      doc.text(toPdfText(`Schnittkante · rechts anschliessend: Blatt ${right.nr}`), view.x + w + 2.5, view.y + 1, {
        angle: -90,
      });
    }
    const below = cellAt(cell.r + 1, cell.c);
    if (below && !below.empty) {
      doc.line(view.x, view.y + h, view.x + w, view.y + h);
      doc.text(toPdfText(`Schnittkante · unten anschliessend: Blatt ${below.nr}`), view.x, view.y + h + 3, {
        baseline: "top",
      });
    }
    // Überlappung: gepunktete Linie, wo das vorangehende Blatt abgeschnitten
    // wird — dort wird es aufgelegt.
    if (t.overlap > 0) {
      doc.setLineDashPattern([0.4, 0.8], 0);
      const left = cellAt(cell.r, cell.c - 1);
      if (left && !left.empty) doc.line(view.x + t.overlap, view.y, view.x + t.overlap, view.y + h);
      const above = cellAt(cell.r - 1, cell.c);
      if (above && !above.empty) doc.line(view.x, view.y + t.overlap, view.x + w, view.y + t.overlap);
    }
    doc.setLineDashPattern([], 0);
  });
  doc.save(`Mindmap_${filenamePart(title)}_${format}_${t.count}Blaetter.pdf`);
}
